import {useEffect, useMemo, useState} from 'react';
import { SearchSuggestion } from '@src/types/Filters';
import { Suggestions } from '../Suggestions';
import { CreatorItem } from '../FilterItem/CreatorItem';
import { useAtomValue } from 'jotai';
import {
    FacetState,
} from '@src/pages/Discover/components/Atoms/Atoms';
import { useMediaFindFacet } from "@src/queries/media/useQueryMediaFind";
import { onFilterChange } from "@src/fragments/Filters/utils/FilterStateUtility";
import { XclusionUtility } from "@src/fragments/Filters/managers/XclusionUtility";
import {creatorInput, creatorExcludeState} from "@src/pages/Discover/components/Selectors/Selectors";
import { useFiltersParams } from '@src/hooks/useFiltersParams';
import { normalizeCreatorUsername } from '@src/utils/creatorUsername';

interface Props {
    influencers?: SearchSuggestion[],
}

export function CreatorsSuggestion({ influencers }: Props) {
  const { filters, setFilters } = useFiltersParams();
  const facetState = useAtomValue(FacetState);

  const filterCreatorInput = useAtomValue(creatorInput);
  const exclude = useAtomValue(creatorExcludeState);
  const blacklistedCreators = facetState.mediaConstraints.blacklistedCreators;
  const blacklistedCreatorUsernames = useMemo(
    () => new Set((blacklistedCreators ?? []).map(normalizeCreatorUsername)),
    [blacklistedCreators],
  );

  const {
    data: resFilterCreator,
    isLoading: isLoadingFilterCreator,
  } = useMediaFindFacet(
      facetState.mediaConstraints,
      filterCreatorInput,
      'creators',
      facetState.typingDebounce,
      facetState.enableFacet,
  );
 const creators = resFilterCreator?.pages[0].facets?.creators;
 const [suggestions, setSuggestions] = useState(influencers? influencers
     : creators?.map(
      (value) => ({
        key: value,
        display: `${value}`,
      }),
  ) ?? [])

  useEffect(() => {
      if (!influencers && !isLoadingFilterCreator && resFilterCreator?.pages[0].facets?.creators) {
          setSuggestions(resFilterCreator?.pages[0].facets?.creators.map(
              (value) => ({
                  key: value,
                  display: `${value}`,
              }),
          ))
      }
  }, [isLoadingFilterCreator, resFilterCreator])

  const selectedSuggestions = influencers
    ? influencers.filter((inf) => filters.creator.find((c) => XclusionUtility.getRawKey(c) === XclusionUtility.getRawKey(inf.key)))
    : filters.creator.map((value) => ({
        key: value,
        display: `${XclusionUtility.getRawKey(value)}`,
      }));

  const blacklistLoaded = blacklistedCreators !== undefined;
  const filteredSelectedSuggestions = blacklistLoaded
    ? filterBlacklistedCreatorSuggestions(selectedSuggestions, blacklistedCreatorUsernames)
    : [];
  const availableSuggestions = blacklistLoaded
    ? filterBlacklistedCreatorSuggestions(suggestions, blacklistedCreatorUsernames)
    : [];

  return (
    <Suggestions
      onChange={onFilterChange(setFilters, 'creator')}
      selectedValues={renderValues(filteredSelectedSuggestions)}
      values={renderValues(availableSuggestions)}
      exclude={exclude}
    />
  );
}

function renderValues(suggestions: SearchSuggestion[]) {
  return suggestions.map((suggestion) => ({
    key: suggestion.key,
    children: (
      <CreatorItem suggestion={suggestion} />
    ),
  }));
}

export function filterBlacklistedCreatorSuggestions(
  suggestions: SearchSuggestion[],
  blacklistedCreatorUsernames: ReadonlySet<string>,
): SearchSuggestion[] {
  return suggestions.filter(
    ({ key }) => !blacklistedCreatorUsernames.has(
      normalizeCreatorUsername(XclusionUtility.getRawKey(key)),
    ),
  );
}
